import React from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"

import { ConfirmationOverlay } from "./ConfirmationOverlay"

export interface ConfirmationOverlayPayload {
  id: string
  headline: string
  message: React.ReactNode // Supports both strings and JSX
  isDanger?: boolean
  isLoading?: boolean
  confirmButtonText?: string
  cancelButtonText?: string
  autoFocus?: boolean
  onConfirm: () => void
  // Note: onCancel not needed - closeOverlay handles this
}

export interface DefaultConfirmationOverlayProps {
  payload: ConfirmationOverlayPayload
}

export const DefaultConfirmationOverlay = React.memo(
  ({ payload }: DefaultConfirmationOverlayProps) => {
    const { closeOverlay } = useCurrentOverlay()
    const {
      id,
      headline,
      message,
      isDanger = false,
      isLoading = false,
      confirmButtonText,
      cancelButtonText,
      autoFocus = true,
      onConfirm
    } = payload

    const handleConfirm = useEvent(() => {
      onConfirm()
      closeOverlay()
    })

    return (
      <ConfirmationOverlay.Root
        id={id}
        isDanger={isDanger}
        isLoading={isLoading}
        autoFocus={autoFocus}
      >
        <ConfirmationOverlay.Header>
          <ConfirmationOverlay.Title>{headline}</ConfirmationOverlay.Title>
        </ConfirmationOverlay.Header>
        <ConfirmationOverlay.Message>{message}</ConfirmationOverlay.Message>
        <ConfirmationOverlay.Actions>
          <ConfirmationOverlay.Cancel onClick={closeOverlay}>
            {cancelButtonText}
          </ConfirmationOverlay.Cancel>
          <ConfirmationOverlay.Submit onClick={handleConfirm}>
            {confirmButtonText}
          </ConfirmationOverlay.Submit>
        </ConfirmationOverlay.Actions>
      </ConfirmationOverlay.Root>
    )
  }
)
DefaultConfirmationOverlay.displayName = "DefaultConfirmationOverlay"
